import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  Provider,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideAdclMaterial } from '@adcl/ngx-material';
import { provideAdclIcons } from '@adcl/ngx-icons';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { HttpXsrfInterceptor } from './http-interceptor/http-xsrf-interceptor';
import { environment } from '../environments/environment';
import { NGX_PENDO_SETTINGS_TOKEN, NGX_PENDO_WINDOW, pendoInitializer, provideNgxPendo } from 'ngx-pendo';

const PENDO_INIT_TIMEOUT_MS = 5000;

// ngx-pendo types `pendoInitializerProvider` as `Provider`, but it is fed straight into the
// library's own `makeEnvironmentProviders([...])` call, which also accepts `EnvironmentProviders`.
const pendoInitializerProvider = provideAppInitializer(() => {
  const settings = inject(NGX_PENDO_SETTINGS_TOKEN);
  const window = inject(NGX_PENDO_WINDOW);
  return Promise.race([
    pendoInitializer(settings, window)(),
    new Promise<void>(resolve => setTimeout(resolve, PENDO_INIT_TIMEOUT_MS)),
  ]);
}) as unknown as Provider;

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'de',
      })
    ),
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { color: 'primary' } },
    { provide: MatBottomSheetRef, useValue: {} },
    { provide: MAT_BOTTOM_SHEET_DATA, useValue: {} },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpXsrfInterceptor,
      multi: true,
    },

    provideNgxPendo({
      pendoApiKey: environment.PENDO_KEY,
      pendoIdFormatter: (pendoId: string) => pendoId.toLowerCase(),
      pendoInitializerProvider,
    }),

    provideHttpClient(withInterceptorsFromDi()),
    provideAdclMaterial({ autoUpdateThemeForPreferredColorScheme: true }),
    provideAdclIcons({ assetsUrl: 'assets/adcl-icons/', registerIcons: 'single-files' }),
    provideRouter(routes),
  ],
};
